import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RoleBadge from '../components/RoleBadge';
import '../Admin.css';
import './Users.css';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const { currentUser } = useAuth();

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = await currentUser.getIdToken();
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data.users);
            setError('');
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Erro ao carregar usuários. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);
    const handleDeleteUser = async (uid) => {
        if (!window.confirm('Tem certeza que deseja deletar este usuário?')) {
            return;
        }

        try {
            const token = await currentUser.getIdToken();
            await axios.delete(`${process.env.REACT_APP_API_URL}/users/${uid}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchUsers();
        } catch (err) {
            console.error('Error deleting user:', err);
            alert('Erro ao deletar usuário');
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setShowModal(true);
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setShowModal(true);
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.displayName || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = filterRole === 'all' || user.customClaims?.role === filterRole;
        return matchesSearch && matchesRole;
    });

    return (
        <>
            {loading ? (
                <div className="loading">
                    <i className="fas fa-spinner fa-spin"></i> Carregando usuários...
                </div>
            ) : (
                <div className="users-container">
                    <div className="users-header">
                        <div>
                            <h1><i className="fas fa-users"></i> Usuários</h1>
                            <p>Gerencie admins e usuários com acesso ao painel</p>
                        </div>
                        <button className="btn-add-user" onClick={handleAddUser}>
                            <i className="fas fa-plus"></i> Novo Usuário
                        </button>
                    </div>

                    {error && (
                        <div className="error-message">
                            <i className="fas fa-exclamation-circle"></i> {error}
                        </div>
                    )}

                    <div className="users-filters">
                        <div className="search-box">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Buscar por email ou nome..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="filter-select"
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                        >
                            <option value="all">Todos os Roles</option>
                            <option value="admin">Admin</option>
                            <option value="editor">Editor</option>
                            <option value="viewer">Viewer</option>
                        </select>
                    </div>

                    <div className="users-table-container">
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Nome</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Último Login</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="no-results">
                                            Nenhum usuário encontrado
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map(user => (
                                        <tr key={user.uid}>
                                            <td>{user.email}</td>
                                            <td>{user.displayName || '-'}</td>
                                            <td>
                                                <RoleBadge role={user.customClaims?.role || 'viewer'} />
                                            </td>
                                            <td>
                                                <span className={`status-badge ${user.disabled ? 'disabled' : 'active'}`}>
                                                    {user.disabled ? 'Desativado' : 'Ativo'}
                                                </span>
                                            </td>
                                            <td>
                                                {user.metadata.lastSignInTime
                                                    ? new Date(user.metadata.lastSignInTime).toLocaleDateString('pt-BR')
                                                    : 'Nunca'
                                                }
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => handleEditUser(user)}
                                                        title="Editar"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDeleteUser(user.uid)}
                                                        title="Deletar"
                                                        disabled={user.uid === currentUser.uid}
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {showModal && (
                        <UserModal
                            user={editingUser}
                            onClose={() => setShowModal(false)}
                            onSuccess={() => {
                                setShowModal(false);
                                fetchUsers();
                            }}
                        />
                    )}
                </div>
            )}
        </>
    );
};

// UserModal Component
const UserModal = ({ user, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        email: user?.email || '',
        displayName: user?.displayName || '',
        password: '',
        role: user?.customClaims?.role || 'viewer'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { currentUser } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = await currentUser.getIdToken();

            if (user) {
                // Update existing user
                const updateData = {
                    displayName: formData.displayName
                };
                if (formData.password) {
                    updateData.password = formData.password;
                }

                await axios.put(`${process.env.REACT_APP_API_URL}/users/${user.uid}`, updateData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Update role if changed
                if (formData.role !== user.customClaims?.role) {
                    await axios.put(`${process.env.REACT_APP_API_URL}/users/${user.uid}/role`,
                        { role: formData.role },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                }
            } else {
                // Create new user
                await axios.post(`${process.env.REACT_APP_API_URL}/users`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            onSuccess();
        } catch (err) {
            console.error('Error saving user:', err);
            setError(err.response?.data?.error || 'Erro ao salvar usuário');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>
                        <i className={`fas ${user ? 'fa-edit' : 'fa-plus'}`}></i>
                        {user ? 'Editar Usuário' : 'Novo Usuário'}
                    </h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {error && (
                    <div className="modal-error">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Email *</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            disabled={!!user}
                        />
                    </div>

                    <div className="form-group">
                        <label>Nome</label>
                        <input
                            type="text"
                            value={formData.displayName}
                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        />
                    </div>

                    <div className="form-group">
                        <label>Senha {!user && '*'}</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required={!user}
                            placeholder={user ? 'Deixe em branco para manter' : 'Mínimo 6 caracteres'}
                        />
                    </div>

                    <div className="form-group">
                        <label>Role *</label>
                        <select
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            required
                        >
                            <option value="admin">Admin - Acesso total</option>
                            <option value="editor">Editor - Gerenciar ofertas</option>
                            <option value="viewer">Viewer - Apenas visualizar</option>
                        </select>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-submit" disabled={loading}>
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Salvando...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-check"></i> {user ? 'Atualizar' : 'Criar'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Users;
